import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import GoogleRating from "components/basic/googleRating";
import {
  GoogleListingStatus as ListingStatus,
  useVendorGoogleListingQuery,
} from "generated/graphql";

interface GoogleListingStatusProps {
  vendorId: number;
  /** Bumped by the parent after a save so the listing is re-read. */
  refreshKey: number;
}

/**
 * Which Google listing the vendor's link resolved to and the rating customers
 * see, so a wrong match is caught here rather than on a quote.
 */
const GoogleListingStatus: React.FC<GoogleListingStatusProps> = ({
  vendorId,
  refreshKey,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("profile");
  const [{ data, fetching, error }, reexecuteQuery] =
    useVendorGoogleListingQuery({
      variables: { id: vendorId },
      requestPolicy: "network-only",
    });

  useEffect(() => {
    if (refreshKey) {
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  }, [refreshKey, reexecuteQuery]);

  const vendor = data?.vendor;
  const listing = vendor?.googleListing;
  const listingName = listing?.placeName ?? t("google_status.your_listing");
  const hasStars = Boolean(listing?.rating && listing.reviewCount > 0);

  const status = (() => {
    if (fetching && !data) return t("google_status.checking");
    if (error) return t("google_status.check_failed");
    if (!vendor || vendor.googleListingStatus === ListingStatus.NotLinked) {
      return t("google_status.not_linked");
    }
    if (vendor.googleListingStatus === ListingStatus.Retired) {
      return t("google_status.retired");
    }
    if (vendor.googleListingStatus === ListingStatus.PendingReview) {
      return t("google_status.pending");
    }
    if (vendor.googleListingStatus === ListingStatus.Rejected) {
      return t("google_status.rejected");
    }
    if (!listing) {
      return t("google_status.unreachable");
    }
    if (!hasStars) {
      return t("google_status.no_reviews", { name: listingName });
    }
    return t("google_status.linked", { name: listingName });
  })();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        px: 2,
        pb: 1,
        textAlign: "center",
      }}
    >
      <Typography
        variant="body2"
        align="center"
        style={{ color: theme.palette.text.secondary }}
      >
        {status}
      </Typography>
      <GoogleRating listing={listing} />
    </Box>
  );
};

export default GoogleListingStatus;
